import { useState, useEffect, useCallback } from 'react';

// Saved words live in this browser's localStorage only: no account, no sync.
// If storage is blocked (private mode), they last until the tab closes.
const KEY = 'savedWords';
const EVENT = 'savedwordschange';
let memory = null;

const read = () => {
  if (memory) return memory;
  try {
    const saved = JSON.parse(localStorage.getItem(KEY) || '[]');
    memory = Array.isArray(saved) ? saved : [];
  } catch {
    memory = [];
  }
  return memory;
};

const write = (words) => {
  memory = words;
  try {
    localStorage.setItem(KEY, JSON.stringify(words));
  } catch {
    // Kept in memory for this visit
  }
  window.dispatchEvent(new Event(EVENT));
};

export const useSavedWords = () => {
  const [saved, setSaved] = useState(read);

  useEffect(() => {
    const sync = () => setSaved([...read()]);
    // Another tab changed the list
    const onStorage = (e) => {
      if (e.key === KEY) {
        memory = null;
        sync();
      }
    };
    window.addEventListener(EVENT, sync);
    window.addEventListener('storage', onStorage);
    return () => {
      window.removeEventListener(EVENT, sync);
      window.removeEventListener('storage', onStorage);
    };
  }, []);

  const isSaved = useCallback((word) => saved.includes(word), [saved]);

  const toggle = useCallback((word) => {
    const current = read();
    write(current.includes(word) ? current.filter((w) => w !== word) : [word, ...current]);
  }, []);

  return { saved, isSaved, toggle };
};
